<template>
  <div class="wrapper">
    <div class="container">
      <div class="sub-header">
        <p>
          {{ parseTranslation(HOME) }} >
          <span>{{ parseTranslation(REPORT) }}</span>
        </p>

        <div>
          <h1>{{ parseTranslation(SURVEY_RESULTS) }}</h1>

          <router-link
            :to="{ name: 'quota', query: { survey: $route.query.survey } }"
            class="redirect-to-quota"
            v-if="!isVisitorAccess"
          >
            <Out />

            Sample/{{ parseTranslation(QUOTA) }}</router-link
          >
        </div>
      </div>

      <div class="visitor-link" v-if="!isVisitorAccess">
        <h3>Visitor Link</h3>

        <div class="visitor-btn-wrapper">
          <button
            @click="visitorId ? copyVisitorLink() : createVisistorLink()"
            class="visitor-button"
          >
            {{ parseTranslation(visitorId ? COPY : GENERATE) }}
          </button>

          <span class="visitor-text" v-if="visitorId">{{ visitorLink }}</span>
        </div>
      </div>

      <div v-if="!loading">
        <div v-if="hasReport">
          <div class="survey-name-container">
            <h2>{{ surveyStatus.name }}</h2>

            <router-link to="/" v-if="!isVisitorAccess">
              <BackArrowIcon />
              {{ parseTranslation(ALL_SURVEYS) }}
            </router-link>

            <div v-if="sampleStatus" class="status">
              <span :class="{ paused: sampleStatus === 'pause' }">{{
                parseTranslation(sampleStatus === 'live' ? LIVE : PAUSED)
              }}</span>
              <div class="status-info">
                <p
                  v-html="
                    parseTranslation(
                      sampleStatus === 'live' ? SAMPLE_LIVE_INFO : SAMPLE_PAUSED_INFO,
                    )
                  "
                ></p>
              </div>
            </div>
          </div>

          <ReportSelector :selectedReport="selectedReport" :changeReport="changeReport" />

          <ReportTypeRenderer
            :report_type="selectedReport"
            :survey_status="surveyStatus"
            :get_current_status="getCurrentStatus"
          />
        </div>

        <div v-else>
          {{ parseTranslation(NO_REPORTS) }}
        </div>
      </div>

      <Loader v-else-if="loading" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import copyToClipboard from 'copy-to-clipboard'

import Loader from '@/components/loading/Loader'

import BackArrowIcon from '@/components/imgs/BackArrow.vue'
import Out from '@/components/imgs/Out'

import ReportSelector from './ReportSelector.vue'
import ReportTypeRenderer from './ReportTypeRenderer'

import { removeUndefinedProperties } from '@/utils/object'

import { REPORT_TYPES } from './report.constants'

import * as translationIndex from './index.translations'

const { default: translations, ...translationKeys } = translationIndex

export default {
  name: 'Report',

  components: {
    ReportSelector,
    ReportTypeRenderer,

    Loader,
    BackArrowIcon,
    Out,
  },

  data() {
    return {
      translations,
      ...translationKeys,
      surveyStatus: null,
      loading: false,
      hasReport: false,
      selectedReport: REPORT_TYPES.STATS,
      sampleStatus: null,
      loadingLink: false,
      visitorId: null,
    }
  },

  computed: {
    ...mapGetters(['isVisitorAccess']),

    visitorLink() {
      if (!this.visitorId) return ''

      return `${location.origin}/report?visitor=${this.visitorId}&survey=${this.$route.query.survey}`
    },
  },

  methods: {
    ...mapActions(['executeSurveyAPICall', 'sendMessage']),

    async getCurrentStatus() {
      this.loading = true

      const { survey, visitor } = this.$route.query || {}

      const surveyStatus = await this.executeSurveyAPICall({
        path: `report/status${this.isVisitorAccess ? '/visitor' : ''}`,
        queryParams: removeUndefinedProperties({
          survey_id: survey,
          visitor: this.isVisitorAccess ? visitor : undefined,
        }),
      })

      if (!surveyStatus) {
        this.loading = false
        this.surveyStatus = null

        return
      }

      this.hasReport = true

      this.surveyStatus = surveyStatus

      this.sampleStatus = surveyStatus?.sampleStatus

      this.loading = false

      this.visitorId = surveyStatus.visitorId
    },

    copyVisitorLink() {
      copyToClipboard(this.visitorLink)

      this.sendMessage(this.parseTranslation(this.COPIED))
    },

    async createVisistorLink() {
      this.loading = true

      const data = await this.executeSurveyAPICall({
        method: 'POST',
        path: 'survey/visitor/enable',
        payload: { survey_id: this.$route.query.survey },
      })

      if (!data) return (this.loading = false)

      this.visitorId = data.visitorId
      this.loading = false
    },

    changeReport(reportType) {
      if (this.loading) return

      this.selectedReport = reportType
    },
  },

  created() {
    this.getCurrentStatus()
  },
}
</script>

<style lang="scss" scoped>
.sub-header {
  padding: 30px 0 20px;

  h1 {
    @include tipography(26);
    font-weight: bold;
  }

  div {
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    p {
      @include tipography(16);

      margin: 3px 0 0 15px;

      @include d(s) {
        margin: 10px 0 0;
      }
    }

    @media (max-width: 550px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  > p {
    @include tipography(14);
    color: $gray-2;
    span {
      color: $white;
    }
  }

  .light & {
    border-bottom: 1px solid $gray-5;

    h1,
    span {
      color: $gray-6;
    }

    p {
      color: $gray;
    }
  }
}

.visitor-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid $white;

  .light & {
    border-bottom: 1px solid $gray-5;
  }

  h3 {
    @include tipography(22);

    font-weight: 500;

    .light & {
      color: $gray;
    }
  }

  button {
    border: none;
    outline: none;
    background: $blue;
    color: #fff;
    padding: 3px 8px;
    border-radius: 6px;
  }

  .visitor-text {
    margin-left: 12px;
    max-width: 320px;
    display: inline-block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    .light & {
      color: $gray;
    }
  }

  @media (max-width: 450px) {
    flex-direction: column;
  }
}

.redirect-to-quota {
  @include tipography(14);
  @include flexAlignCenter;

  svg {
    @include boxSizing(14);
    margin-right: 6px;
  }

  .light & {
    color: $gray-4;
  }

  @media (max-width: 550px) {
    margin-top: 16px;
  }
}

.settings-btn {
  @include flexAlignCenter;
  border: none;
  outline: none;
  background: $blue;
  padding: 8px 15px;
  color: #fff;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: bold;
  transition: background-color 0.2s ease;

  @media (max-width: 550px) {
    display: none;
  }

  &:hover {
    background-color: $blue-3;
  }

  svg {
    margin-right: 8px;
    @include boxSizing(18);
  }
}

.survey-name-container {
  @include flexAlignCenter;
  margin-top: 24px;
  color: $white;

  @media (max-width: 550px) {
    flex-direction: column;
    align-items: flex-start;
  }

  h2 {
    @include tipography(24);
    color: $white;
    font-weight: 700;
    margin-right: 16px;
    text-transform: uppercase;

    @media (max-width: 550px) {
      order: 1;
    }
  }

  a {
    @include flexAlignCenter;
    font-size: rem(14);
    transition: opacity 0.2s ease;
    color: $white;

    svg {
      margin-right: 6px;
    }

    .light & {
      color: $gray-4;
    }

    &:hover {
      opacity: 0.8;
    }

    @media (max-width: 550px) {
      order: 0;
      margin-bottom: 16px;
    }
  }

  .status {
    @include sample-status;

    @media (max-width: 550px) {
      order: 2;
      margin: 16px 0 0;

      .status-info {
        left: 0;
        width: 180px;
      }
    }
  }

  .light & {
    color: $gray-11;

    h2 {
      color: $gray-11;
    }
  }
}
</style>
